/**
 * TitleCell — `.title-cell` from the prototype (cms-redesign-mockup.html
 * ~line 1167–1265). A 48×48 cover thumbnail + title + optional state-badge
 * cluster. Used in the Sessions table and any other list whose primary
 * column is a piece of content with a cover image.
 *
 *   <TitleCell
 *     coverImage="https://…/cover.jpg"      // optional URL
 *     coverInitials="CA"                     // fallback initials
 *     coverSeed="Calm Anxiety"               // seed for gradient pick
 *     title="Calm Anxiety in Under 10 Minutes"
 *     badges={[{ label: 'Rec', tone: 'recommended' }]}
 *   />
 *
 * Behavior of the cover (`.cover`, prototype 1167–1186):
 *   • 48×48, rounded-md, inset 1px highlight + soft drop shadow
 *   • If `coverImage` URL is set → background-image cover/center
 *   • Otherwise → linear gradient picked deterministically from the seed,
 *     and the initials sit on top in Asap Condensed 15/700 white
 *   • A 135° linear-gradient overlay (`.cover::after`) adds a subtle sheen
 */
import React from 'react';
import { cn } from './cn';

/**
 * `.c-1` … `.c-8` palette from the prototype. Each entry is a CSS gradient.
 * We pick deterministically from `seed` so a session named "Calm Anxiety"
 * always renders with the same tile color across renders / sessions.
 */
const COVER_GRADIENTS = [
  'linear-gradient(135deg, #4A7A8E, #2C5566)',  // c-1 — slate teal
  'linear-gradient(135deg, #C4715B, #A85A48)',  // c-2 — terracotta
  'linear-gradient(135deg, rgb(var(--color-navy)), rgb(var(--color-navy-deep)))',  // c-3 — navy
  'linear-gradient(135deg, rgb(var(--color-teal-green)), rgb(var(--color-teal-green-dark)))',  // c-5 — teal-green
  'linear-gradient(135deg, rgb(var(--color-coral)), rgb(var(--color-coral-dark)))',  // c-6 — coral
  'linear-gradient(135deg, rgb(var(--color-teal-bright)), #128090)',  // c-7 — bright teal
  'linear-gradient(135deg, #87A878, #6F8C63)',  // c-8 — sage
];

function pickGradient(seed) {
  if (!seed) return COVER_GRADIENTS[0];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return COVER_GRADIENTS[h % COVER_GRADIENTS.length];
}

/** `.state-badge` (prototype 1264–1289). Three variants: recommended (teal
 *  with ★ glyph), duplicate (amber outline), featured (coral with ✦). */
const BADGE_STYLE = {
  recommended: {
    className: 'bg-teal-green-soft text-teal-green-dark',
    prefix: '★',
  },
  duplicate: {
    className: 'border border-[#C28A2C44] text-[#C28A2C]',
    style: { background: '#C28A2C1A' },
    prefix: null,
  },
  featured: {
    className: 'bg-coral-soft text-coral-dark',
    prefix: '✦',
  },
  new: {
    className: 'bg-coral text-white',
    prefix: null,
  },
};

function StateBadge({ tone, label }) {
  const meta = BADGE_STYLE[tone] || BADGE_STYLE.recommended;
  return (
    <span
      className={cn(
        'inline-flex items-center shrink-0 font-condensed font-bold uppercase rounded-sm',
        meta.className,
      )}
      style={{
        gap: 3,
        padding: '1px 6px',
        fontSize: 9.5,
        letterSpacing: '0.06em',
        lineHeight: 1.4,
        ...(meta.style || {}),
      }}
    >
      {meta.prefix && <span style={{ fontSize: 10 }}>{meta.prefix}</span>}
      {label}
    </span>
  );
}

export function TitleCell({
  coverImage,
  coverInitials,
  coverSeed,
  title,
  badges = [],
  className,
}) {
  const initials = (coverInitials || title || '').slice(0, 2).toUpperCase();
  const gradient = pickGradient(coverSeed || initials);

  return (
    // `.title-cell` (1188): flex items-center, gap 14, min-w 0.
    <div className={cn('flex items-center min-w-0', className)} style={{ gap: 14 }}>
      {/* `.cover` — 48×48, rounded-md, inset highlight + soft drop shadow.
       *  Image when present, gradient + initials otherwise. The ::after
       *  sheen overlay is the absolutely-positioned span at the end. */}
      <div
        className="relative shrink-0 grid place-items-center overflow-hidden"
        style={{
          width: 48,
          height: 48,
          borderRadius: 8,
          background: coverImage ? undefined : gradient,
          backgroundImage: coverImage ? `url('${coverImage}')` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          boxShadow: 'inset 0 0 0 1px #00000010, 0 1px 3px #2C3E5018',
        }}
      >
        {!coverImage && (
          <span
            className="font-condensed font-bold text-white relative"
            style={{ fontSize: 15, zIndex: 1 }}
          >
            {initials}
          </span>
        )}
        {/* `.cover::after` — 135° gradient sheen. */}
        <span
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, #FFFFFF28, transparent 50%, #00000018)',
          }}
        />
      </div>

      {/* `.title-text` (1189–1195): min-w 0, headline with title + badges. */}
      <div className="min-w-0 flex-1">
        <div
          className="flex items-center min-w-0"
          style={{ gap: 6 }}
        >
          {/* `.title-name` — 14.5px navy 700, letter-spacing -0.005em,
           *  ellipsis at 380px (we let flexbox handle the ellipsis instead). */}
          <span
            className="font-bold text-navy truncate min-w-0"
            style={{ fontSize: 14.5, letterSpacing: '-0.005em' }}
          >
            {title}
          </span>
          {badges.map((b, i) => (
            <StateBadge key={i} tone={b.tone} label={b.label} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default TitleCell;
